import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../../../contexts/ToastContext';
import GestionPedidoBase from '../shared/GestionPedidoBase';
import MeseroGridSelector from '../../shared/selectors/SeleccionMeseroHorizontal';
import API from '../../../../utils/api';
import './PedidoDomicilio.css';

/**
 * Componente para crear/editar pedidos a domicilio
 * Usa GestionPedidoBase para la lógica centralizada con configuración específica para domicilio
 * 
 * Flujo específico para domicilio:
 * 1. Solicitar teléfono
 * 2. Buscar si existe cliente con ese teléfono
 * 3. Si existe: cargar datos automáticamente
 * 4. Si no existe: solicitar datos completos
 */
const CreacionPedidoDomicilio = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const inicializacionCompleta = useRef(false);

  // ===== ESTADOS ESPECÍFICOS DE PEDIDOS A DOMICILIO =====
  const [clienteNombre, setClienteNombre] = useState('');
  const [clienteTelefono, setClienteTelefono] = useState('');
  const [clienteDireccion, setClienteDireccion] = useState('');
  const [clienteFraccionamiento, setClienteFraccionamiento] = useState('');
  const [clienteReferencia, setClienteReferencia] = useState('');
  const [clienteCompleto, setClienteCompleto] = useState('');

  // Estados para el mesero (consistente con PedidosMesa)
  const [mesero, setMesero] = useState(null);
  const [modalMeseroVisible, setModalMeseroVisible] = useState(false);

  // Estados para el flujo de búsqueda por teléfono
  const [modalBusquedaTelefono, setModalBusquedaTelefono] = useState(false);
  const [telefonoBusqueda, setTelefonoBusqueda] = useState('');
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [clienteEncontrado, setClienteEncontrado] = useState(null);

  // Estados para el modal de datos completos (cuando no se encuentra cliente)
  const [modalClienteVisible, setModalClienteVisible] = useState(false);
  const [clienteNombreTemp, setClienteNombreTemp] = useState('');
  const [clienteTelefonoTemp, setClienteTelefonoTemp] = useState('');
  const [clienteDireccionTemp, setClienteDireccionTemp] = useState('');
  const [clienteFraccionamientoTemp, setClienteFraccionamientoTemp] = useState('');
  const [clienteReferenciaTemp, setClienteReferenciaTemp] = useState('');

  // Estados del pedido específicos para domicilio
  const [modoEdicion, setModoEdicion] = useState(false);
  const [pedidoExistente, setPedidoExistente] = useState(null);
  const [numeroCuenta, setNumeroCuenta] = useState('00');
  const [fechaHora, setFechaHora] = useState(new Date());

  // ===== EFECTOS =====

  // Efecto para actualizar fecha y hora
  useEffect(() => {
    const interval = setInterval(() => {
      setFechaHora(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Cargar datos iniciales al montar
  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  // ===== FUNCIONES DE CARGA DE DATOS =====

  const cargarDatosIniciales = useCallback(async () => {
    if (inicializacionCompleta.current) return;

    try {
      // Verificar datos del estado de navegación (modo edición)
      const datosGuardados = location.state?.datosPedido;

      if (datosGuardados) {
        const datos = datosGuardados;

        // Configurar modo edición si hay un pedido existente
        if (datos.esEdicion && datos.pedidoCreado) {
          setModoEdicion(true);

          // Cargar datos frescos del pedido desde el API para obtener campos como liquidado
          let pedidoActualizado = datos.pedidoCreado;
          try {
            const pedidoFresco = await API.pedidos.getPedidoDomicilio(datos.pedidoCreado.idPedido);
            console.log('🔄 Pedido fresco cargado desde API:', pedidoFresco);

            // Combinar datos existentes con datos frescos del API
            pedidoActualizado = {
              ...datos.pedidoCreado,
              ...pedidoFresco,
              // Preservar algunos campos del localStorage si es necesario
              numeroDia: datos.pedidoCreado.numeroDia || pedidoFresco.numeroDia
            };

            setPedidoExistente(pedidoActualizado);
            setNumeroCuenta(pedidoActualizado.numeroDia || '00');
          } catch (error) {
            console.error('Error al cargar pedido fresco:', error);
            // Fallback a datos del localStorage
            setPedidoExistente(datos.pedidoCreado);
            setNumeroCuenta(datos.pedidoCreado.numeroDia || '00');
          }

          // Extraer información del cliente domicilio - usar datos actualizados
          let nombreCliente = '';
          let telefonoCliente = '';
          let direccionCliente = '';
          let clienteCompleto = '';

          // Primero intentar usar datos frescos del API si están disponibles
          if (pedidoActualizado.nombreCliente) {
            nombreCliente = pedidoActualizado.nombreCliente;
            telefonoCliente = pedidoActualizado.telefono || '';
            direccionCliente = pedidoActualizado.direccion || '';
            clienteCompleto = pedidoActualizado.clienteDomicilio || `${nombreCliente} - ${direccionCliente}`;
          } else if (pedidoActualizado.cliente && typeof pedidoActualizado.cliente === 'object') {
            // Formato nuevo: cliente es un objeto
            nombreCliente = pedidoActualizado.cliente.nombreCliente || '';
            telefonoCliente = pedidoActualizado.cliente.telefono || '';
            direccionCliente = pedidoActualizado.cliente.direccion || '';
            clienteCompleto = pedidoActualizado.cliente.clienteDomicilio ||
                             `${nombreCliente} - ${direccionCliente}`;
          } else {
            // Formato antiguo: usar clienteLlevar y datos directos
            clienteCompleto = pedidoActualizado.clienteLlevar ||
                             pedidoActualizado.clienteDomicilio ||
                             datos.nombreCliente || '';
            nombreCliente = datos.nombreCliente || datos.cliente || '';
            telefonoCliente = datos.telefono || '';
            direccionCliente = datos.direccion || '';

            // Si clienteCompleto está vacío, construirlo
            if (!clienteCompleto && nombreCliente && direccionCliente) {
              clienteCompleto = `${nombreCliente} - ${direccionCliente}`;
            } else if (!clienteCompleto && nombreCliente) {
              clienteCompleto = nombreCliente;
            }
          }

          setClienteNombre(nombreCliente);
          setClienteTelefono(telefonoCliente);
          setClienteDireccion(direccionCliente);
          setClienteCompleto(clienteCompleto);

          // Actualizar mesero si está disponible en el pedido actualizado
          if (pedidoActualizado.mesero || pedidoActualizado.repartidor) {
            const nombreMesero = pedidoActualizado.mesero || pedidoActualizado.repartidor;
            setMesero({ nombre: nombreMesero });
            console.log('Mesero cargado:', nombreMesero);
          }

          console.log('Datos cargados para edición:', {
            nombreCliente,
            telefonoCliente,
            direccionCliente,
            clienteCompleto,
            mesero: pedidoActualizado.mesero || pedidoActualizado.repartidor
          });
        } else {
          // Modo crear nuevo, pero con datos de cliente si existen
          setClienteNombre(datos.nombreCliente || datos.cliente || '');
          setClienteTelefono(datos.telefono || '');
          setClienteDireccion(datos.direccion || '');

          if (datos.nombreCliente && datos.direccion) {
            setClienteCompleto(`${datos.nombreCliente} - ${datos.direccion}`);
          }

          // Cargar mesero si está disponible en los datos guardados
          if (datos.repartidor) {
            setMesero({ nombre: datos.repartidor });
            console.log('Mesero cargado desde localStorage:', datos.repartidor);
          }
        }
      } else {
        // Si no hay datos guardados, mostrar modal de búsqueda por teléfono
        setTimeout(() => {
          setModalBusquedaTelefono(true);
        }, 500);
      }

      inicializacionCompleta.current = true;
    } catch (err) {
      console.error("Error al cargar datos iniciales:", err);
      toast.error("Error al cargar datos iniciales del pedido");
    }
  }, [toast]);
  const abrirModalMesero = useCallback(() => {
    setModalMeseroVisible(true);
  }, []);
  // ===== FUNCIONES DE BÚSQUEDA POR TELÉFONO =====

  const buscarClientePorTelefono = useCallback(async () => {
    if (!telefonoBusqueda.trim()) {
      toast.warning("Ingrese un número de teléfono");
      return;
    }

    try {
      setBuscandoCliente(true);
      const resultado = await API.pedidos.buscarClientePorTelefono(telefonoBusqueda.trim());

      if (resultado.encontrado) {
        // Cliente encontrado - pre-llenar datos y mostrar modal para edición
        setClienteEncontrado(resultado);

        // Pre-llenar los campos temporales del modal con los datos encontrados
        setClienteNombreTemp(resultado.nombreCliente);
        setClienteTelefonoTemp(resultado.telefono);
        setClienteDireccionTemp(resultado.direccion);
        setClienteReferenciaTemp(resultado.referencia || '');
        setClienteFraccionamientoTemp(resultado.fraccionamiento || '');

        setModalBusquedaTelefono(false);

        // Mostrar el modal de datos para que pueda editar la dirección
        setTimeout(() => {
          setModalClienteVisible(true);
        }, 300);

        toast.success(`Cliente encontrado: ${resultado.nombreCliente}. Verifique/edite la dirección.`);

        // Mostrar información del último pedido si está disponible
        if (resultado.ultimoPedido) {
          const fecha = new Date(resultado.ultimoPedido).toLocaleDateString();
          toast.info(`Último pedido: ${fecha}`);
        }
      } else {
        // Cliente no encontrado - solicitar datos completos
        setClienteEncontrado(null);
        setClienteTelefonoTemp(telefonoBusqueda.trim()); // Pre-llenar el teléfono

        setModalBusquedaTelefono(false);
        setTimeout(() => {
          setModalClienteVisible(true);
        }, 300);

        toast.info("Cliente no encontrado. Ingrese los datos completos.");
      }
    } catch (error) {
      console.error("Error al buscar cliente:", error);
      toast.error("Error al buscar cliente por teléfono");
    } finally {
      setBuscandoCliente(false);
    }
  }, [telefonoBusqueda, toast]);

  const cancelarBusquedaTelefono = useCallback(() => {
    setModalBusquedaTelefono(false);
    navigate('/ventas');
  }, [navigate]);
  const quitarMesero = useCallback(() => {
    setMesero(null);
    toast.info('Mesero removido del pedido');
  }, [toast]);
  // ===== CONFIGURACIÓN PARA GESTIÓN PEDIDO BASE =====

  // Configuración específica para pedidos a domicilio
  const configuracion = useMemo(() => ({
    permitirCambioMesa: false,           // No aplica para domicilio
    permitirCambioMesero: false,         // No hay mesero en domicilio
    permitirCambioPersonas: false,       // Siempre 1 persona para domicilio
    permitirDatosCliente: true,          // SÍ necesita datos de cliente
    permitirDireccion: true,             // SÍ necesita dirección de entrega
    permitirRepartidor: false,           // NO tiene repartidor (se asignará después)
    requiereImpresion: true,             // SÍ requiere impresión de comandas
    permitirPagoMultiple: true           // SÍ permite múltiples formas de pago
  }), []);

  // Datos del pedido para GestionPedidoBase
  const datosPedido = useMemo(() => {
    const datos = {
      idPedido: pedidoExistente?.idPedido || null,
      numeroPedido: numeroCuenta,
      status: modoEdicion ? (pedidoExistente?.status || 'pendiente') : 'nuevo',
      liquidado: pedidoExistente?.liquidado || '0', // Incluir campo liquidado
      // Datos específicos de pedido a domicilio
      clienteDomicilio: clienteCompleto,
      nombreCliente: clienteNombre,
      telefono: clienteTelefono,
      direccion: clienteDireccion,
      fraccionamiento: clienteFraccionamiento,
      referencia: clienteReferencia,
      numeroPersonas: 1, // Siempre 1 para domicilio
      fechaCreacion: fechaHora,
      // Para compatibilidad con infoPedido en TomaPedidos
      numeroMesa: 'DOMICILIO',
      nombreMesa: 'Pedido a Domicilio',
      mesa: {
        numero: 'DOMICILIO',
        nombre: 'Pedido a Domicilio'
      },
      mesero: mesero, // Mesero asignado al pedido a domicilio
      // Información del cliente para mostrar en header
      infoCliente: {
        nombre: clienteNombre,
        telefono: clienteTelefono,
        direccion: clienteDireccion,
        fraccionamiento: clienteFraccionamiento,
        referencia: clienteReferencia
      },
      // Callback para editar mesero (consistente con PedidosMesa)
      onEditarMesero: abrirModalMesero
    };

    return datos;
  }, [pedidoExistente, numeroCuenta, modoEdicion, clienteCompleto, clienteNombre, clienteTelefono, clienteDireccion, clienteFraccionamiento, clienteReferencia, fechaHora, mesero, abrirModalMesero]);

  // ===== CALLBACKS PARA GESTIÓN PEDIDO BASE =====

  const onPedidoCreado = useCallback(async (nuevoPedido) => {
    try {
      toast.success("Pedido a domicilio creado correctamente");

      // Actualizar estados locales con el nuevo pedido
      if (nuevoPedido.numeroDia) {
        setNumeroCuenta(nuevoPedido.numeroDia);
      }

      // IMPORTANTE: Actualizar pedidoExistente con el nuevo pedido que incluye idPedido
      if (nuevoPedido.idPedido) {
        setPedidoExistente(nuevoPedido);
        setModoEdicion(true); // Cambiar a modo edición ya que ahora existe el pedido

        // Actualizar el history state para que si navegamos a pago y regresamos,
        // location.state tenga los datos del pedido y no muestre el modal de búsqueda
        navigate(location.pathname, {
          replace: true,
          state: {
            datosPedido: {
              esEdicion: true,
              pedidoCreado: nuevoPedido,
              nombreCliente: clienteNombre,
              telefono: clienteTelefono,
              direccion: clienteDireccion
            }
          }
        });
      }

      // Intentar actualizar datos del cliente si hay un endpoint específico
      if (clienteNombre && clienteDireccion) {
        try {
          // Actualizar datos del pedido a domicilio con información del cliente
          await API.pedidos.actualizarPedidoDomicilio(nuevoPedido.idPedido, {
            clienteDomicilio: clienteCompleto,
            nombreCliente: clienteNombre,
            telefono: clienteTelefono,
            direccion: clienteDireccion
          });
        } catch (error) {
          console.error("Error al actualizar datos del cliente:", error);
          // No mostrar error al usuario, es información adicional
        }
      }
    } catch (error) {
      console.error("Error en onPedidoCreado:", error);
    }
  }, [toast, clienteNombre, clienteTelefono, clienteDireccion, clienteCompleto, navigate, location.pathname]);

  const onPedidoActualizado = useCallback((pedidoActualizado) => {
    toast.success("Pedido a domicilio actualizado correctamente");

    // Actualizar el estado local del pedido con los nuevos datos
    if (pedidoActualizado && pedidoExistente) {
      setPedidoExistente(prev => ({
        ...prev,
        ...pedidoActualizado
      }));
    }
  }, [toast, pedidoExistente]);

  const onPedidoCerrado = useCallback((resultado) => {
    toast.success("Pedido a domicilio finalizado correctamente");

    // Actualizar el estado local del pedido con el nuevo status
    if (resultado && pedidoExistente) {
      const pedidoActualizado = {
        ...pedidoExistente,
        status: 'finalizado' // Pedidos a domicilio se cambian a finalizado al cerrar
      };
      setPedidoExistente(pedidoActualizado);

      // Actualizar history state con el status actualizado
      navigate(location.pathname, {
        replace: true,
        state: {
          datosPedido: {
            esEdicion: true,
            pedidoCreado: pedidoActualizado,
            nombreCliente: clienteNombre,
            telefono: clienteTelefono,
            direccion: clienteDireccion
          }
        }
      });
    }
  }, [toast, pedidoExistente, navigate, location.pathname, clienteNombre, clienteTelefono, clienteDireccion]);

  // ===== MANEJADORES ESPECÍFICOS DEL MODAL DE CLIENTE =====

  const confirmarCliente = useCallback(async () => {
    if (!clienteNombreTemp.trim()) {
      toast.warning("El nombre del cliente es obligatorio");
      return;
    }

    if (!clienteDireccionTemp.trim()) {
      toast.warning("La dirección de entrega es obligatoria");
      return;
    }

    const nombreLimpio = clienteNombreTemp.trim();
    const telefonoLimpio = clienteTelefonoTemp.trim();
    const direccionLimpia = clienteDireccionTemp.trim();
    const fraccionamientoLimpio = clienteFraccionamientoTemp.trim();
    const referenciaLimpia = clienteReferenciaTemp.trim();

    // Construir dirección completa con fraccionamiento
    let direccionCompleta = direccionLimpia;
    if (fraccionamientoLimpio) {
      direccionCompleta = `${direccionLimpia}, ${fraccionamientoLimpio}`;
    }

    try {
      // Guardar o actualizar cliente en la base de datos
      const resultadoGuardado = await API.pedidos.guardarCliente({
        nombre: nombreLimpio,
        telefono: telefonoLimpio,
        direccion: direccionCompleta,
        referencia: referenciaLimpia
      });

      console.log('Cliente guardado:', resultadoGuardado);

      const clienteCompletoNuevo = `${nombreLimpio} - ${direccionCompleta}`;

      setClienteNombre(nombreLimpio);
      setClienteTelefono(telefonoLimpio);
      setClienteDireccion(direccionLimpia);
      setClienteFraccionamiento(fraccionamientoLimpio);
      setClienteReferencia(referenciaLimpia);
      setClienteCompleto(clienteCompletoNuevo);
      setModalClienteVisible(false);

      if (resultadoGuardado.esNuevo) {
        toast.success(`Nuevo cliente registrado: ${nombreLimpio}`);
      } else {
        toast.success(`Cliente actualizado: ${nombreLimpio}`);
      }
    } catch (error) {
      console.error('Error al guardar cliente:', error);
      toast.error('Error al guardar cliente en la base de datos');
      // Aún así guardamos localmente para continuar con el pedido
      const clienteCompletoNuevo = `${nombreLimpio} - ${direccionCompleta}`;
      setClienteNombre(nombreLimpio);
      setClienteTelefono(telefonoLimpio);
      setClienteDireccion(direccionLimpia);
      setClienteFraccionamiento(fraccionamientoLimpio);
      setClienteReferencia(referenciaLimpia);
      setClienteCompleto(clienteCompletoNuevo);
      setModalClienteVisible(false);
    }
  }, [clienteNombreTemp, clienteTelefonoTemp, clienteDireccionTemp, clienteFraccionamientoTemp, clienteReferenciaTemp, toast]);

  const cancelarCliente = useCallback(() => {
    if (!clienteCompleto) {
      // Si no hay cliente configurado, regresar a búsqueda por teléfono
      setModalClienteVisible(false);
      setTimeout(() => {
        setModalBusquedaTelefono(true);
      }, 300);
      return;
    }

    setModalClienteVisible(false);
    // Restaurar valores originales
    setClienteNombreTemp(clienteNombre);
    setClienteTelefonoTemp(clienteTelefono);
    setClienteDireccionTemp(clienteDireccion);
    setClienteFraccionamientoTemp(clienteFraccionamiento);
    setClienteReferenciaTemp(clienteReferencia);
  }, [clienteCompleto, clienteNombre, clienteTelefono, clienteDireccion, clienteFraccionamiento, clienteReferencia]);

  const editarCliente = useCallback(() => {
    setClienteNombreTemp(clienteNombre);
    setClienteTelefonoTemp(clienteTelefono);
    setClienteDireccionTemp(clienteDireccion);
    setClienteFraccionamientoTemp(clienteFraccionamiento);
    setClienteReferenciaTemp(clienteReferencia);
    setModalClienteVisible(true);
  }, [clienteNombre, clienteTelefono, clienteDireccion, clienteFraccionamiento, clienteReferencia]);

  // ===== FUNCIONES DE MESEROS (consistente con PedidosMesa) =====



  const seleccionarMesero = useCallback((meseroSeleccionado) => {
    setMesero(meseroSeleccionado);
    setModalMeseroVisible(false);
    toast.success(`Mesero asignado: ${meseroSeleccionado.nombre}`);
  }, [toast]);

  const cerrarModalMesero = useCallback(() => {
    setModalMeseroVisible(false);
  }, []);



  // ===== VALIDACIÓN DE CLIENTE Y DIRECCIÓN =====

  const validarClienteYDireccion = useCallback(() => {
    if (!clienteCompleto || !clienteNombre || !clienteDireccion) {
      toast.error("Debe proporcionar los datos del cliente y la dirección de entrega");
      setModalBusquedaTelefono(true);
      return false;
    }
    return true;
  }, [clienteCompleto, clienteNombre, clienteDireccion, toast]);

  // ===== RENDER =====

  // Si no hay cliente configurado y no está mostrando modales, mostrar carga
  if (!clienteCompleto && !modalBusquedaTelefono && !modalClienteVisible) {
    console.log("clienteCompleto", clienteCompleto);
    console.log("modalBusquedaTelefono", modalBusquedaTelefono);
    console.log("modalClienteVisible", modalClienteVisible);


    return (
      <div className="toma-pedidos-cargando">
        <div className="spinner"></div>
        <p>Configurando pedido a domicilio...</p>
      </div>
    );
  }

  return (
    <>
      {/* Usar GestionPedidoBase con configuración específica para domicilio */}
      {clienteCompleto && (
        <GestionPedidoBase
          tipoPedido="domicilio"
          datosPedido={datosPedido}
          configuracion={configuracion}
          onPedidoCreado={onPedidoCreado}
          onPedidoActualizado={onPedidoActualizado}
          onPedidoCerrado={onPedidoCerrado}
          rutaRegreso="/ventas"
          // Validaciones personalizadas
          validacionesPersonalizadas={{
            antesDeGuardar: validarClienteYDireccion,
            antesDeImprimir: validarClienteYDireccion,
            antesDeFinalizar: validarClienteYDireccion
          }}
          // Función para mostrar/editar cliente
          onEditarCliente={editarCliente}
        />
      )}

      {/* Modal de búsqueda por teléfono - PASO 1 */}
      {modalBusquedaTelefono && (
        <div className="modal-overlay">
          <div className="modal-container modal-busqueda-telefono">
            <div className="modal-header">
              <h2>Pedido a Domicilio</h2>
              <button
                className="close-button"
                onClick={cancelarBusquedaTelefono}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="busqueda-info">
                <h3>🔍 Buscar Cliente</h3>
                <p>Ingrese el número de teléfono para buscar datos existentes:</p>
              </div>

              <div className="form-group">
                <label htmlFor="telefonoBusquedaInput">
                  Número de Teléfono *
                </label>
                <input
                  type="tel"
                  id="telefonoBusquedaInput"
                  value={telefonoBusqueda}
                  onChange={(e) => setTelefonoBusqueda(e.target.value)}
                  className="telefono-input"
                  placeholder="Ej: 5551234567"
                  autoFocus
                  maxLength="15"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !buscandoCliente) {
                      buscarClientePorTelefono();
                    }
                  }}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="search-button"
                onClick={buscarClientePorTelefono}
                disabled={!telefonoBusqueda.trim() || buscandoCliente}
              >
                {buscandoCliente ? (
                  <>
                    <div className="spinner-small"></div>
                    Buscando...
                  </>
                ) : (
                  <>
                    🔍 Buscar Cliente
                  </>
                )}
              </button>
              <button
                className="cancel-button"
                onClick={cancelarBusquedaTelefono}
                disabled={buscandoCliente}
              >
                ✗ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de datos completos del cliente - PASO 2 (si no se encontró) */}
      {modalClienteVisible && (
        <div className="modal-overlay">
          <div className="modal-container modal-cliente-domicilio">
            <div className="modal-header">
              <h2>Datos del Cliente - Pedido a Domicilio</h2>
              <button
                className="close-button"
                onClick={cancelarCliente}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="nuevo-cliente-info">
                <h3>{clienteEncontrado ? '✏️ Editar Cliente' : '📝 Nuevo Cliente'}</h3>
                <p>{clienteEncontrado ? 'Verifique y edite los datos del cliente:' : 'Complete los datos del cliente:'}</p>
              </div>

              <div className="form-group">
                <label htmlFor="nombreClienteInput">
                  Nombre del Cliente *
                </label>
                <input
                  type="text"
                  id="nombreClienteInput"
                  value={clienteNombreTemp}
                  onChange={(e) => setClienteNombreTemp(e.target.value)}
                  className="cliente-input"
                  placeholder="Ingrese el nombre del cliente"
                  autoFocus
                  maxLength="100"
                />
              </div>

              <div className="form-group">
                <label htmlFor="telefonoClienteInput">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="telefonoClienteInput"
                  value={clienteTelefonoTemp}
                  onChange={(e) => setClienteTelefonoTemp(e.target.value)}
                  className="cliente-input"
                  placeholder="Número de teléfono"
                  maxLength="15"
                />
              </div>

              <div className="form-group">
                <label htmlFor="direccionClienteInput">
                  Dirección de Entrega *
                </label>
                <input
                  type="text"
                  id="direccionClienteInput"
                  value={clienteDireccionTemp}
                  onChange={(e) => setClienteDireccionTemp(e.target.value)}
                  className="cliente-input"
                  placeholder="Calle y número"
                  maxLength="200"
                />
              </div>

              <div className="form-group">
                <label htmlFor="fraccionamientoClienteInput">
                  Colonia / Fraccionamiento (opcional)
                </label>
                <input
                  type="text"
                  id="fraccionamientoClienteInput"
                  value={clienteFraccionamientoTemp}
                  onChange={(e) => setClienteFraccionamientoTemp(e.target.value)}
                  className="cliente-input"
                  placeholder="Nombre de la colonia o fraccionamiento"
                  maxLength="100"
                />
              </div>

              <div className="form-group">
                <label htmlFor="referenciaClienteInput">
                  Referencias (opcional)
                </label>
                <textarea
                  id="referenciaClienteInput"
                  value={clienteReferenciaTemp}
                  onChange={(e) => setClienteReferenciaTemp(e.target.value)}
                  className="cliente-input"
                  placeholder="Ej: Casa blanca con portón negro, cerca del parque"
                  maxLength="250"
                  rows="2"
                />
              </div>

              <div className="cliente-preview">
                <strong>Se guardará como:</strong>
                <span>
                  {clienteNombreTemp.trim() && clienteDireccionTemp.trim() ?
                    `${clienteNombreTemp.trim()} - ${clienteDireccionTemp.trim()}${clienteFraccionamientoTemp.trim() ? ', ' + clienteFraccionamientoTemp.trim() : ''}` :
                    'Ingrese nombre y dirección'
                  }
                </span>
                {clienteTelefonoTemp.trim() && (
                  <div className="info-adicional">
                    📞 Teléfono: {clienteTelefonoTemp.trim()}
                  </div>
                )}
                {clienteFraccionamientoTemp.trim() && (
                  <div className="info-adicional">
                    🏘️ Colonia: {clienteFraccionamientoTemp.trim()}
                  </div>
                )}
                {clienteReferenciaTemp.trim() && (
                  <div className="info-adicional">
                    📍 Referencias: {clienteReferenciaTemp.trim()}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="accept-button"
                onClick={confirmarCliente}
                disabled={!clienteNombreTemp.trim() || !clienteDireccionTemp.trim() || !clienteTelefonoTemp.trim()}
              >
                ✓ Confirmar
              </button>
              <button
                className="cancel-button"
                onClick={cancelarCliente}
              >
                ← Regresar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de selección de meseros (consistente con PedidosMesa) */}
      {modalMeseroVisible && (
        <div className="modal-overlay">
          <div className="modal-container">
            <MeseroGridSelector
              onMeseroSelect={seleccionarMesero}
              onCancel={cerrarModalMesero}
              meseroActual={mesero}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CreacionPedidoDomicilio;